import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Shield, CheckCircle, AlertCircle, Users, TrendingUp, Heart } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

export const LoginForm: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailValid, setEmailValid] = useState<boolean | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuthStore();

  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Real-time email validation
  useEffect(() => {
    if (formData.email) {
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      setEmailValid(isValid);
    } else {
      setEmailValid(null);
    }
  }, [formData.email]);

  // Password strength calculation
  useEffect(() => {
    if (formData.password) {
      let strength = 0;
      if (formData.password.length >= 6) strength += 25;
      if (formData.password.length >= 8) strength += 25;
      if (/[A-Z]/.test(formData.password)) strength += 25;
      if (/[0-9]/.test(formData.password)) strength += 25;
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(0);
    }
  }, [formData.password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err: any) {
      let errorMessage = 'Une erreur est survenue lors de la connexion';
      
      // Priorité 1: Vérifier si c'est notre erreur personnalisée avec code et message
      if (err && err.name === 'AuthError' && err.code && err.message) {
        errorMessage = err.message;
      }
      // Priorité 2: Vérifier le message direct de l'erreur
      else if (err && err.message) {
        errorMessage = err.message;
      }
      // Fallback: Parser err.body si présent (pour compatibilité)
      else if (err.body && typeof err.body === 'string') {
        try {
          const parsedBody = JSON.parse(err.body);
          if (parsedBody.message) {
            errorMessage = parsedBody.message;
          }
        } catch (parseError) {
          // Si le parsing échoue, garder le message par défaut
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-red-500';
    if (passwordStrength < 50) return 'bg-orange-500';
    if (passwordStrength < 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 25) return 'Faible';
    if (passwordStrength < 50) return 'Moyen';
    if (passwordStrength < 75) return 'Bon';
    return 'Excellent';
  };

  const floatingMessages = [
    { text: "🌱 Cultiver l'économie solidaire", delay: 0, position: 'top-20 left-10' },
    { text: "🤝 Ensemble, créons du lien", delay: 2, position: 'top-40 right-20' },
    { text: "💡 Innovation sociale", delay: 4, position: 'top-60 left-1/4' },
    { text: "🌍 Impact positif local", delay: 1, position: 'bottom-40 right-1/4' },
    { text: "♻️ Économie circulaire", delay: 3, position: 'bottom-60 left-1/3' },
    { text: "🎯 Projet à impact", delay: 5, position: 'top-1/3 right-10' },
    { text: "🌟 Accompagnement dédié", delay: 2.5, position: 'bottom-20 left-20' },
    { text: "💚 Solidarité territoriale", delay: 4.5, position: 'top-1/2 left-10' },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50">
        {/* Floating Messages with Themes ESS */}
        {floatingMessages.map((msg, index) => (
          <div
            key={index}
            className={`absolute ${msg.position} animate-float-message opacity-0`}
            style={{
              animationDelay: `${msg.delay}s`,
              animationDuration: '8s',
              animationIterationCount: 'infinite'
            }}
          >
            <div className="bg-white/80 backdrop-blur-md px-4 py-2 rounded-full shadow-lg border border-blue-100 hover:scale-110 hover:bg-white/90 transition-all duration-300 cursor-default">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {msg.text}
              </span>
            </div>
          </div>
        ))}

        {/* Floating geometric shapes */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-teal-400/20 to-cyan-400/20 rounded-full blur-xl animate-pulse" />
        <div className="absolute top-40 right-20 w-48 h-48 bg-gradient-to-br from-blue-400/15 to-cyan-400/15 rounded-full blur-2xl animate-bounce" style={{ animationDuration: '3s' }} />
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-gradient-to-br from-cyan-400/25 to-teal-400/25 rounded-full blur-lg animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-40 right-1/3 w-36 h-36 bg-gradient-to-br from-blue-400/20 to-teal-400/20 rounded-full blur-xl animate-bounce" style={{ animationDuration: '4s', animationDelay: '2s' }} />

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-8">
        <div className={`max-w-md w-full transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {/* Header with Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="relative group">
                <img
                  src="/cresslogo.png"
                  alt="CRESS Guadeloupe"
                  className="h-32 w-auto object-contain transform transition-all duration-500 group-hover:scale-110 drop-shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-teal-400/20 to-cyan-400/20 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-teal-700 bg-clip-text text-transparent mb-3 animate-fade-in">
              Bienvenue
            </h1>
            <p className="text-gray-600 text-lg animate-slide-up" style={{ animationDelay: '0.2s' }}>
              Plateforme d'accompagnement ESS Guadeloupe
            </p>
          </div>

          {/* Stats Cards with Enhanced Animation */}
          <div className="grid grid-cols-3 gap-3 mb-8 animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 backdrop-blur-md rounded-xl p-3 text-center border border-blue-200/50 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-1 group">
              <Users className="w-5 h-5 text-blue-600 mx-auto mb-1 group-hover:scale-110 transition-transform" />
              <div className="text-lg font-bold bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent">150+</div>
              <div className="text-xs text-gray-700 font-medium">Projets</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-teal-50 backdrop-blur-md rounded-xl p-3 text-center border border-green-200/50 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-1 group">
              <TrendingUp className="w-5 h-5 text-green-600 mx-auto mb-1 group-hover:scale-110 transition-transform" />
              <div className="text-lg font-bold bg-gradient-to-r from-green-700 to-teal-600 bg-clip-text text-transparent">85%</div>
              <div className="text-xs text-gray-700 font-medium">Réussite</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-red-50 backdrop-blur-md rounded-xl p-3 text-center border border-orange-200/50 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-1 group">
              <Heart className="w-5 h-5 text-red-500 mx-auto mb-1 group-hover:scale-110 transition-transform" />
              <div className="text-lg font-bold bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-transparent">2.4M€</div>
              <div className="text-xs text-gray-700 font-medium">Financés</div>
            </div>
          </div>

          {/* Login Form */}
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8 animate-scale-in" style={{ animationDelay: '0.6s' }}>
            <div className="mb-6">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent mb-2">
                Bienvenue
              </h2>
              <p className="text-gray-600">Connectez-vous pour accéder à la plateforme</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50/90 backdrop-blur-sm border border-red-200 rounded-xl animate-shake">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
                  Adresse email
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-blue-500" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:border-gray-300 hover:shadow-md text-gray-900 placeholder-gray-500"
                    placeholder="votre@email.com"
                  />
                  {emailValid !== null && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      {emailValid ? (
                        <CheckCircle className="w-5 h-5 text-green-500 animate-scale-in" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500 animate-shake" />
                      )}
                    </div>
                  )}
                </div>
                {formData.email && !emailValid && (
                  <p className="text-xs text-red-600 animate-slide-down">Format d'email invalide</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                  Mot de passe
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-blue-500" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:border-gray-300 hover:shadow-md text-gray-900 placeholder-gray-500"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all duration-200 hover:scale-110"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="space-y-2 animate-slide-down">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Force du mot de passe</span>
                      <span className={`font-medium ${
                        passwordStrength >= 75 ? 'text-green-600' :
                        passwordStrength >= 50 ? 'text-yellow-600' :
                        passwordStrength >= 25 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {getPasswordStrengthLabel()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-2 rounded-full transition-all duration-500 ${getPasswordStrengthColor()}`}
                        style={{ width: `${passwordStrength}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 transition-all duration-200"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                    Se souvenir de moi
                  </span>
                </label>
                
                <Link
                  to="/reset-password"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-all duration-200 hover:underline hover:scale-105"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !emailValid}
                className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-teal-600 text-white py-4 px-6 rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-teal-700 focus:ring-4 focus:ring-blue-500/50 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 font-semibold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transform"
              >
                {isLoading ? (
                  <>
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Connexion...</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    <span>Se connecter</span>
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="my-8 flex items-center">
              <div className="flex-1 border-t border-gray-200" />
              <span className="px-4 text-sm text-gray-500 bg-white/50 rounded-full">ou</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Quick Links */}
            <div className="space-y-3">
              <Link
                to="/advisor-signup"
                className="w-full flex items-center justify-center space-x-2 py-3 px-4 border-2 border-blue-200 text-blue-700 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 hover:scale-105 font-medium"
              >
                <Users className="w-5 h-5" />
                <span>Devenir accompagnateur ESS</span>
              </Link>
              
              <Link
                to="/register"
                className="w-full flex items-center justify-center space-x-2 py-3 px-4 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 hover:scale-105 font-medium"
              >
                <span>Créer un compte porteur</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Testimonial */}
          <div className="mt-8 bg-white/70 backdrop-blur-md rounded-2xl p-6 border border-white/40 shadow-lg animate-fade-in" style={{ animationDelay: '0.8s' }}>
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 text-yellow-400 animate-pulse" style={{ animationDelay: `${i * 0.1}s` }}>
                      ⭐
                    </div>
                  ))}
                </div>
              </div>
              <blockquote className="text-gray-700 italic mb-3 leading-relaxed">
                "Grâce à l'accompagnement CRESS, j'ai pu concrétiser mon projet d'agriculture solidaire.
                Un soutien précieux et personnalisé du début à la fin !"
              </blockquote>
              <cite className="text-sm font-semibold bg-gradient-to-r from-blue-700 to-teal-600 bg-clip-text text-transparent">
                Marie D., Porteur de projet Agriculture Bio
              </cite>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500 animate-fade-in" style={{ animationDelay: '1s' }}>
            <p>© 2024 CRESS Guadeloupe. Tous droits réservés.</p>
            <div className="mt-3 flex justify-center space-x-6">
              <Link to="/legal/privacy" className="hover:text-gray-700 transition-colors duration-200 hover:underline">
                Confidentialité
              </Link>
              <Link to="/legal/terms" className="hover:text-gray-700 transition-colors duration-200 hover:underline">
                Conditions d'utilisation
              </Link>
              <Link to="/legal/accessibility" className="hover:text-gray-700 transition-colors duration-200 hover:underline">
                Accessibilité
              </Link>
            </div>
          </div>

          {/* Contact Info */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 mb-2">Besoin d'aide ?</p>
            <div className="flex justify-center space-x-4 text-xs">
              <a 
                href="mailto:support@cress-guadeloupe.org" 
                className="text-blue-600 hover:text-blue-700 transition-colors duration-200 hover:underline"
              >
                📧 support@cress-guadeloupe.org
              </a>
              <a 
                href="tel:+590590123456" 
                className="text-blue-600 hover:text-blue-700 transition-colors duration-200 hover:underline"
              >
                📞 05 90 12 34 56
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};